import { useIntl } from 'react-intl';

import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { useTooltipSeriesActions } from '../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import type { SeriesActionsTemplate } from '../../../core/types/series-actions-template.js';
import { useGaugeChartTooltip } from '../../contexts/GaugeChartTooltipContext.js';
import { useGaugeChartFormatterContext } from '../../hooks/useGaugeChartFormatterContext.js';
import type { GaugeChartTooltipItem } from '../../slots/Tooltip.js';

interface GaugeOverlayTooltipProps {
  value: number;
  color: string;
}

export const GaugeOverlayTooltip = ({
  value,
  color,
}: GaugeOverlayTooltipProps) => {
  const intl = useIntl();
  const { formatter, unit } = useGaugeChartFormatterContext();
  const { name: tooltipName, template } = useGaugeChartTooltip();

  const seriesName =
    tooltipName ??
    intl.formatMessage({ defaultMessage: 'Value', id: 'jc4XDeI4sf7aLs2W' });
  const formattedValue = formatter(value, unit);

  const tooltipItem = {
    id: `${seriesName}-${value}`,
    name: seriesName,
    values: [formattedValue],
  };

  const seriesActions = useTooltipSeriesActions(
    [tooltipItem],
    (item) => item.id,
  );

  const actions = (
    tooltipItem ? () => seriesActions(tooltipItem) : undefined
  ) as (() => SeriesActionsTemplate) | undefined;

  if (template) {
    const customItem: GaugeChartTooltipItem = {
      value,
      formattedValue,
      color,
      name: tooltipName,
      unit: typeof unit === 'string' ? unit : undefined,
    };
    return <OverlayTooltip legacyTemplate={template(customItem)} />;
  }

  return (
    <OverlayTooltip>
      <OverlayTooltip.SeriesItem
        name={seriesName}
        value={formattedValue}
        color={color}
        actions={actions}
      />
    </OverlayTooltip>
  );
};

GaugeOverlayTooltip['displayName'] = 'GaugeOverlayTooltip';
